import { FC, Fragment, useMemo } from "react";
import { Box } from "@mui/material";
import { calculateTotalCostTrend } from "../../../../pages/utils/helpers/calculateTotalCostTrend";
import { WidgetTotalsSummary } from "../../../../pages/dashboard/components/widgets/common/WidgetTotalsSummary";
import { ResourceExplorerGridDataWithId } from "@/store/resource-explorer/utils/types/resourceExplorerDataGrid";
import {
  DateDataPointNullable,
  DateLabelNullable,
  ForecastedCost,
  ForecastOption,
  ResourceExplorerGroupingNullable,
  WidgetCostBreakdownAggregation,
  WidgetCostBreakdownVisualization,
} from "@/services/cloudchipr.api";
import { WidgetCosts } from "@/store/dashboards/utils/types/types";
import { BillingExplorerWidgetAgGrid } from "@/components/pages/dashboard/components/widgets/billing-explorer-widget/components/chart/ag-table/BillingExplorerWidgetAgGrid";

interface UnauthorizedBillingExplorerWidgetTableViewProps {
  originalDataCount?: number;
  gridData?: ResourceExplorerGridDataWithId[];
  grouping: ResourceExplorerGroupingNullable;
  forecastOption: ForecastOption;
  forecastedCosts?: ForecastedCost[];
  widgetCosts?: WidgetCosts;
  visualization: WidgetCostBreakdownVisualization;
  dateLabel?: DateLabelNullable;
  dateDataPoint: DateDataPointNullable;
  aggregation: WidgetCostBreakdownAggregation;
}

export const UnauthorizedBillingExplorerWidgetTableView: FC<
  UnauthorizedBillingExplorerWidgetTableViewProps
> = ({
  forecastOption,
  grouping,
  gridData,
  forecastedCosts,
  originalDataCount,
  widgetCosts,
  visualization,
  dateLabel,
  dateDataPoint,
  aggregation,
}) => {
  const forecastedCost = useMemo(
    () =>
      forecastedCosts?.find((item) => item.forecast_option === forecastOption),
    [forecastedCosts, forecastOption],
  );

  const trend = useMemo(() => {
    const total = widgetCosts?.total.cost ?? 0;
    const previous = widgetCosts?.previousPeriodTotal?.cost ?? 0;

    return previous > 0 ? calculateTotalCostTrend(total, previous) : null;
  }, [widgetCosts?.total.cost, widgetCosts?.previousPeriodTotal?.cost]);

  return (
    <Fragment>
      <Box p={2}>
        {dateDataPoint && forecastOption && widgetCosts && (
          <WidgetTotalsSummary
            frequency={dateDataPoint}
            costsData={widgetCosts}
            forecastOption={forecastOption}
            dateLabel={dateLabel}
            showTrends={visualization.trend}
            aggregations={aggregation}
          />
        )}
      </Box>

      <BillingExplorerWidgetAgGrid
        totalTrend={trend}
        grouping={grouping}
        data={gridData ?? []}
        visualization={visualization}
        forecastOption={forecastOption}
        forecastedCost={forecastedCost}
        totalItemsCount={originalDataCount ?? 0}
        totalCostDates={widgetCosts?.total?.dateDetails}
        prevCostDates={widgetCosts?.previousPeriodTotal?.dateDetails}
      />
    </Fragment>
  );
};
